import React, { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ButtonDropdown } from "@amzn/awsui-components-react/polaris";

import { RootState } from "../../app/rootReducer";
import { trackButtonClick } from "../../telemetry/telemetry";
import { FullAsinWithAudits } from "../../../from-server/asin-central/model";
import { formatMatchCount } from "./formatMatchCount";
import {
  MAX_BULK_PASS_EXCEPTION_ASINS,
  openAndFetch,
  openWithAsins,
  PassExceptionMode,
} from "./submitPassExceptionsConnector";

const BASE_LABEL = "Submit Pass Exceptions";
const ZERO_DISABLED_REASON = "No ASINs match the current filter";
const ERROR_DISABLED_REASON = "Match count unavailable";
const LOADING_PERMISSIONS_DISABLED_REASON = "Loading permissions...";
const NO_PERMISSION_DISABLED_REASON =
  "You do not have permission to create pass exceptions. Contact the LEAP team.";
const OVER_CAP_DISABLED_REASON = `Too many ASINs — narrow the filter or select up to ${MAX_BULK_PASS_EXCEPTION_ASINS.toLocaleString(
  "en-US"
)} rows`;
const REGULAR_ITEM_ID = "regular";
const ONE_TIME_ITEM_ID = "one_time";

export interface SubmitPassExceptionsButtonProps {
  readonly selectedAsins?: readonly FullAsinWithAudits[];
}

interface DropdownRenderProps {
  readonly label: string;
  readonly disabled: boolean;
  readonly loading: boolean;
  readonly disabledReason?: string;
}

function computeRenderProps(
  effectiveCount: number,
  isLoading: boolean,
  error: string | null,
  hasSelection: boolean,
  authorizationKnown: boolean,
  authorized: boolean
): DropdownRenderProps {
  if (!authorizationKnown) {
    return {
      label: BASE_LABEL,
      disabled: true,
      loading: false,
      disabledReason: LOADING_PERMISSIONS_DISABLED_REASON,
    };
  }
  if (!authorized) {
    return {
      label: BASE_LABEL,
      disabled: true,
      loading: false,
      disabledReason: NO_PERMISSION_DISABLED_REASON,
    };
  }
  if (error !== null && !hasSelection) {
    return {
      label: BASE_LABEL,
      disabled: true,
      loading: false,
      disabledReason: ERROR_DISABLED_REASON,
    };
  }
  if (!hasSelection && isLoading && effectiveCount === 0) {
    return {
      label: BASE_LABEL,
      disabled: false,
      loading: true,
    };
  }
  if (effectiveCount === 0) {
    return {
      label: `${BASE_LABEL} (${formatMatchCount(0)})`,
      disabled: true,
      loading: false,
      disabledReason: ZERO_DISABLED_REASON,
    };
  }
  if (effectiveCount > MAX_BULK_PASS_EXCEPTION_ASINS) {
    return {
      label: `${BASE_LABEL} (${formatMatchCount(effectiveCount)})`,
      disabled: true,
      loading: false,
      disabledReason: OVER_CAP_DISABLED_REASON,
    };
  }
  return {
    label: `${BASE_LABEL} (${formatMatchCount(effectiveCount)})`,
    disabled: false,
    loading: false,
  };
}

export const SubmitPassExceptionsButton: React.FC<
  SubmitPassExceptionsButtonProps
> = ({ selectedAsins }) => {
  const dispatch = useDispatch();
  const count = useSelector((state: RootState) => state.porAsinList.count);
  const isLoading = useSelector(
    (state: RootState) => state.porAsinList.isLoading
  );
  const error = useSelector((state: RootState) => state.porAsinList.error);
  const isModalBusy = useSelector(
    (state: RootState) =>
      state.submitPassExceptions.isOpen ||
      state.submitPassExceptions.isSubmitting
  );
  const editableAttributes = useSelector(
    (state: RootState) => state.porAsinList.editableAttributes
  );
  const editableAttributesLoaded = useSelector(
    (state: RootState) => state.porAsinList.editableAttributesLoaded
  );

  const authorizationKnown = editableAttributesLoaded;
  const authorized = editableAttributes.some(
    (attr) => attr.attributeName === "create_pass_exception" && attr.authorized
  );

  const selection = selectedAsins ?? [];
  const hasSelection = selection.length > 0;
  const effectiveCount = hasSelection ? selection.length : count;

  const { label, disabled, loading, disabledReason } = computeRenderProps(
    effectiveCount,
    isLoading,
    error,
    hasSelection,
    authorizationKnown,
    authorized
  );

  const openModal = useCallback(
    (mode: PassExceptionMode) => {
      trackButtonClick("passException", "OpenBulk", {
        mode,
        selectedCount: selection.length,
        effectiveCount,
      });
      if (hasSelection) {
        dispatch(openWithAsins({ asins: Array.from(selection), mode }));
      } else {
        dispatch(openAndFetch({ mode }));
      }
    },
    [dispatch, hasSelection, selection, effectiveCount]
  );

  const handleItemClick = useCallback(
    (event: { readonly detail: { readonly id: string } }) => {
      if (event.detail.id === ONE_TIME_ITEM_ID) {
        openModal("one_time");
        return;
      }
      openModal("regular");
    },
    [openModal]
  );

  return (
    <ButtonDropdown
      data-testid="submit-pass-exceptions-button"
      variant="normal"
      disabled={disabled || isModalBusy}
      disabledReason={disabledReason}
      loading={loading}
      items={[
        { id: REGULAR_ITEM_ID, text: "Pass Exception" },
        { id: ONE_TIME_ITEM_ID, text: "One-Time Pass Exception" },
      ]}
      onItemClick={handleItemClick}
    >
      {label}
    </ButtonDropdown>
  );
};

export default SubmitPassExceptionsButton;
